import type { FeatureKey, LoginRequest, MeResponse, Permission } from "@station/contracts";
import { useQuery } from "@tanstack/react-query";
import { api, refreshSession } from "./api.js";
import { authStore } from "./auth-store.js";
import { queryClient, queryKeys } from "./query.js";

async function fetchMe(): Promise<MeResponse> {
  const { data } = await api.GET("/me");
  return data as MeResponse;
}

/**
 * `/me` önbellekte varsa onu, yoksa sunucudan döndürür (rota `beforeLoad` kararları için — ör. filo portalı kullanıcısını
 * personel kabuğu çizilmeden `/portal`'a yönlendirmek). `useMe` ile AYNI sorgu anahtarını paylaşır.
 */
export function ensureMe(): Promise<MeResponse> {
  return queryClient.ensureQueryData({ queryKey: queryKeys.me, queryFn: fetchMe, staleTime: 60_000 });
}

/** Profil, üyelikler, etkin kiracı, izinler, açık modüller. Tüm kabuk bu sorgudan beslenir. */
export function useMe() {
  return useQuery({ queryKey: queryKeys.me, queryFn: fetchMe, staleTime: 60_000 });
}

export function usePermissions(): {
  has: (permission: Permission) => boolean;
  hasAny: (...permissions: Permission[]) => boolean;
  set: ReadonlySet<string>;
} {
  const permissions = new Set<string>(useMe().data?.activeTenant?.permissions ?? []);
  return {
    set: permissions,
    has: (permission) => permissions.has(permission),
    hasAny: (...wanted) => wanted.some((permission) => permissions.has(permission)),
  };
}

/** Etkin kiracıda açık olan modüller (özellik bayrakları). Kapalı modülün ekranı ve alanları çizilmez. */
export function useFeatures(): { has: (feature: FeatureKey) => boolean } {
  const features = new Set<string>(useMe().data?.activeTenant?.features ?? []);
  return { has: (feature) => features.has(feature) };
}

/** Uygulama açılışında: httpOnly çerezdeki refresh token ile sessizce oturum açmayı dener. */
export async function bootstrapSession(): Promise<boolean> {
  if (authStore.getState().status === "authenticated") return true;
  return refreshSession();
}

export type LoginResult = { status: "AUTHENTICATED" } | { status: "MFA_REQUIRED"; mfaToken: string };

export async function login(input: LoginRequest): Promise<LoginResult> {
  const { data } = await api.POST("/auth/login", { body: input });
  if (data!.status === "MFA_REQUIRED") return { status: "MFA_REQUIRED", mfaToken: data!.mfaToken };
  authStore.setTokens(data!);
  await queryClient.invalidateQueries();
  return { status: "AUTHENTICATED" };
}

export async function verifyMfa(mfaToken: string, code: string): Promise<void> {
  const { data } = await api.POST("/auth/mfa/verify", { body: { mfaToken, code } });
  authStore.setTokens(data!);
  await queryClient.invalidateQueries();
}

export async function logout(): Promise<void> {
  await api.POST("/auth/logout").catch(() => undefined);
  authStore.clear();
  queryClient.clear();
}

/** Kiracı değiştirme: yeni erişim token'ı alınır ve TÜM önbellek atılır (eski kiracının verisi ekranda kalmasın). */
export async function switchTenant(tenantId: string): Promise<void> {
  const { data } = await api.POST("/auth/switch-tenant", { body: { tenantId } });
  authStore.setTokens(data!);
  queryClient.clear();
}

/**
 * Destek erişimi (Prompt 24, ADR 0024): kiracının onayladığı izinle SALT OKUNUR girilir; oturum o kiracıya bağlanır ve her
 * istek kiracının göreceği kayda yazılır. Kiracı değiştirmedeki gibi önbellek tamamen atılır.
 */
export async function enterSupport(grantId: string): Promise<void> {
  const { data } = await api.POST("/platform/support-grants/{id}/enter", {
    params: { path: { id: grantId } },
  });
  authStore.setTokens(data!);
  queryClient.clear();
}

/** Destek oturumundan çıkış: kiracısız oturuma dönülür (platform paneli). */
export async function exitSupport(): Promise<void> {
  const { data } = await api.POST("/platform/support/exit");
  authStore.setTokens(data!);
  queryClient.clear();
}

export async function reauthenticate(credential: { password?: string; code?: string }): Promise<void> {
  const { data } = await api.POST("/auth/reauthenticate", { body: credential });
  authStore.setTokens(data!);
}
